import { useCallback } from 'react'
import { BlockNoteEditor } from '@packages/blocknote-core'
import { type WindowWithEditor } from '../../../../utils/WindowWithEditor'
import { useEditorContentDispatch, useNeedsUploadDispatch } from '../../../../contexts/EditorContent/hooks'
import { Message } from '../types'

/**
 * Hook for handling editor operations related to AI interactions.
 * Provides functions to retrieve the current note content and apply AI-suggested edits
 * to the BlockNote editor.
 */
export function useEditHandler() {
  const setEditorContent = useEditorContentDispatch()
  const setNeedsUpload = useNeedsUploadDispatch()

  /**
   * Retrieves the current content of the note from the BlockNote editor.
   * Converts the editor blocks to the NotePlan format for AI processing.
   */
  const getNoteContent = useCallback(() => {
    const editor = (window as WindowWithEditor).editor
    if (!editor) {
      console.error('[useEditHandler] Editor not available')
      return null
    }
    
    const blocks = editor.topLevelBlocks
    const content = BlockNoteEditor.blocksToNotePlan(blocks, false)
    return content
  }, [])

  /**
   * Applies an AI-proposed edit to the editor content.
   * Replaces all editor blocks with the modified content from the AI.
   */
  const applyEdit = useCallback((message: Message, onSuccess?: () => void, onError?: (error: string) => void) => {
    if (!message.proposedEdit) {
      const error = 'No proposed edit in message'
      console.error('[useEditHandler]', error)
      onError?.(error)
      return
    }
    
    const editor = (window as WindowWithEditor).editor
    if (!editor) {
      const error = 'Editor not available on window'
      console.error('[useEditHandler]', error)
      onError?.(error)
      return
    }

    console.log('[useEditHandler] Applying edit to editor')
    console.log('[useEditHandler] Current blocks:', editor.topLevelBlocks.length)
    console.log('[useEditHandler] Modified content:', message.proposedEdit.modified)

    try {
      // Parse the new content into blocks - need to handle attachments
      const currentAttachments = '[]' // We'll preserve any existing attachments
      const newBlocks = BlockNoteEditor.notePlanToBlocks(
        message.proposedEdit.modified, 
        currentAttachments
      )
      
      console.log('[useEditHandler] New blocks created:', newBlocks.length)
      
      // Replace all blocks in the editor
      editor.replaceBlocks(editor.topLevelBlocks, newBlocks)
      
      console.log('[useEditHandler] Blocks replaced successfully')
      
      // Update the editor content context to trigger re-renders
      setEditorContent(newBlocks)
      
      // Mark that the content needs to be uploaded/saved
      setNeedsUpload(true)
      
      // Force editor to update its view and focus
      editor._tiptapEditor.commands.focus()
      
      onSuccess?.()
    } catch (error) {
      const errorMessage = String(error)
      console.error('[useEditHandler] Error applying edit:', error)
      onError?.(errorMessage)
    }
  }, [setEditorContent, setNeedsUpload])

  return {
    getNoteContent,
    applyEdit
  }
}